import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';
import '../../styles/AdminShared.css';
import './AdminProfile.css';

const AdminProfile: React.FC = () => {
  const { user, profile } = useAuth();

  // Profile picture
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Password
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Feedback
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (profile?.profile_picture) {
      setPreviewUrl(getImageUrl(profile.profile_picture));
    }
  }, [profile]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // --- Profile Picture ---
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate image type & size
    if (!file.type.startsWith('image/')) {
      showToast('error', 'Please select a valid image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('error', 'Image must be less than 5MB.');
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target?.result as string);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('profile_picture', file);
      await authAPI.updateProfileWithPicture(formData);
      showToast('success', 'Profile picture updated successfully!');
    } catch (err: any) {
      showToast('error', err?.response?.data?.detail || 'Failed to upload picture.');
    } finally {
      setUploading(false);
    }
  };

  // --- Password Change ---
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      showToast('error', "New passwords don't match.");
      return;
    }

    if (newPassword.length < 8) {
      showToast('error', 'Password must be at least 8 characters.');
      return;
    }

    setChangingPassword(true);
    try {
      await authAPI.changePassword(oldPassword, newPassword, confirmPassword);
      showToast('success', 'Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.old_password) {
        showToast('error', data.old_password[0] || 'Current password is incorrect.');
      } else if (data?.new_password) {
        showToast('error', data.new_password[0] || 'Invalid new password.');
      } else {
        showToast('error', 'Failed to change password. Please try again.');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const getInitials = () => {
    const first = user?.first_name?.[0] || '';
    const last = user?.last_name?.[0] || '';
    return (first + last).toUpperCase() || user?.username?.[0]?.toUpperCase() || 'A';
  };

  return (
    <div className="admin-profile-page">
      {/* Toast */}
      {toast && (
        <div className={`toast-message ${toast.type}`}>
          {toast.type === 'success' ? '✅' : '❌'} {toast.message}
        </div>
      )}

      {/* Profile Info Card */}
      <div className="profile-card">
        <h3>👤 Profile Information</h3>

        <div className="avatar-section">
          <div className="avatar-wrapper" onClick={handleAvatarClick}>
            {previewUrl ? (
              <img src={previewUrl} alt="Profile" className="avatar-image" />
            ) : (
              <div className="avatar-placeholder">{getInitials()}</div>
            )}
            <div className="avatar-overlay">
              <span>📷</span>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>

          <div className="avatar-info">
            <h4>{user?.first_name} {user?.last_name}</h4>
            <p>@{user?.username}</p>
            {user?.is_superuser ? (
              <span className="role-badge superadmin">👑 Super Admin</span>
            ) : (
              <span className="role-badge admin">🛡️ Admin</span>
            )}
          </div>
        </div>

        {uploading && <p style={{ color: '#6366f1', fontSize: 13 }}>Uploading picture...</p>}

        <div className="profile-info-grid">
          <div className="info-item">
            <label>Username</label>
            <span>{user?.username}</span>
          </div>
          <div className="info-item">
            <label>Email</label>
            <span>{user?.email}</span>
          </div>
          <div className="info-item">
            <label>First Name</label>
            <span>{user?.first_name || '—'}</span>
          </div>
          <div className="info-item">
            <label>Last Name</label>
            <span>{user?.last_name || '—'}</span>
          </div>
          <div className="info-item">
            <label>Phone</label>
            <span>{profile?.phone || '—'}</span>
          </div>
          <div className="info-item">
            <label>Member Since</label>
            <span>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'}</span>
          </div>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="profile-card">
        <h3>🔒 Change Password</h3>
        <form className="password-form" onSubmit={handlePasswordChange}>
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="Enter current password"
              required
            />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)"
              required
              minLength={8}
            />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={8}
            />
          </div>
          <div className="password-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={changingPassword || !oldPassword || !newPassword || !confirmPassword}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProfile;
